
import React, { useContext, useEffect, useState, useCallback } from "react";
import {
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Switch,
    Typography
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SaveIcon from '@mui/icons-material/Save';
import {
    AboutField,
    EmailField,
    FirstNameField,
    LastNameField,
    PhoneField
} from "../login/LoginFields";
import { ManageUsersContext } from "./ManageUsersContext";
import { GetUserDetails, PatchUserDetails } from "../../utils/AxiosUtils";
import { SiteContext } from "../../utils/SiteContext";
import RefreshButton from "../../components/RefreshButton";
import StyledButton from "../../components/StyledButton";

const Root = styled(Paper)(({ theme }) => ({
    position: "relative",
    padding: 20,
    // margin: 20,
    width: 'auto',
}));

const SaveButton = styled(StyledButton)(({ theme }) => ({
    float: 'right',
}));

const initialErrorForm = {
    firstname: "",
    lastname: "",
    phone: "",
    primary_company: "",
    email: "",
    password: ""
};

const blankUser = {
    id: -1,
    first_name: " ",
    last_name: " ",
    primary_company: " ",
    email: " ",
    phone: " ",
    about: " ",
    password: "testtest",
    is_active: false
};

export default function UserDetails({ user }) {
    const { alert, loading } = useContext(SiteContext);
    const [, setAlertBar] = alert;
    const [loadingVal, setLoading] = loading;
    const [userVal, setUser] = user;

    const [userDetails, setUserDetails] = useState({});
    const [errorForm, setErrorForm] = useState(initialErrorForm);

    const { companies } = useContext(ManageUsersContext);
    const [companiesVal] = companies;

    /** Handlers **/
    const updateField = useCallback((field, value) => {
        setUserDetails(prev => ({ ...prev, [field]: value }));
    }, []);

    const getUserDetails = useCallback(() => {
        if (!userVal) {
            setUserDetails(blankUser);
            return;
        }
        setErrorForm(initialErrorForm);
        GetUserDetails(userVal, setUserDetails);
    }, [userVal]);

    const handleSave = useCallback(() => {
        setErrorForm(initialErrorForm);
        PatchUserDetails(userVal, userDetails, setAlertBar, setLoading, setErrorForm);
    }, [userVal, userDetails, setAlertBar, setLoading]);

    /** Effects **/
    useEffect(() => {
        setErrorForm(initialErrorForm);
        getUserDetails();
    }, [getUserDetails, loadingVal]);

    if (!userDetails) return <Root />;

    return (
        <Root id="userDetailsDiv">
            <Typography variant="subtitle1" sx={{fontWeight: 'bold'}} gutterBottom>
                User Details
            </Typography>

            <RefreshButton id="userDetailRefreshBtn" onClick={getUserDetails} sx={{margin: 2}}/>

            {/* User Detail Fields */}
            <Grid container spacing={1.25}>
                <Grid item sm={12} md={6}>
                    <FirstNameField
                        userDetails={userDetails}
                        onChange={(e) => updateField("first_name", e.target.value)}
                        formCheck={errorForm}
                    />
                </Grid>
                <Grid item sm={12} md={6}>
                    <LastNameField
                        userDetails={userDetails}
                        onChange={(e) => updateField("last_name", e.target.value)}
                        formCheck={errorForm}
                    />
                </Grid>
                <Grid item xs={12}>
                    <EmailField
                        userDetails={userDetails}
                        onChange={(e) => updateField("email", e.target.value)}
                        formCheck={errorForm}
                    />
                </Grid>
                <Grid item xs={12} sm={8}>
                    <FormControl
                        fullWidth
                        error={Boolean(errorForm.primary_company)}
                    >
                        <InputLabel id="select-primary_company-label">Primary Company</InputLabel>
                        <Select
                            labelId="select-primary_company-label"
                            id="primary_company"
                            value={userDetails.primary_company || " "}
                            label="Primary Company"
                            size="small"
                            onChange={(e) => updateField("primary_company", e.target.value)}
                        >
                            <MenuItem key={0} value=" " />
                            {companiesVal.map((c) => (
                                <MenuItem key={c.id} value={c.company_name}>
                                    {c.company_name} ({c.short_name})
                                </MenuItem>
                            ))}
                        </Select>
                        <FormHelperText>{errorForm.primary_company}</FormHelperText>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <PhoneField
                        userDetails={userDetails}
                        onChange={(e) => updateField("phone", e.target.value)}
                        formCheck={errorForm}
                    />
                </Grid>
            </Grid>

            {/* Switches */}
            <FormControlLabel
                // labelPlacement="start"
                control={
                    <Switch
                        checked={Boolean(userDetails.is_active)}
                        onChange={(e) => updateField("is_active", e.target.checked)}
                        color="primary"
                    />
                }
                label="Account Activated"
            />
            <FormControlLabel
                // labelPlacement="start"
                control={
                    <Switch
                        checked={Boolean(userDetails.is_staff)}
                        onChange={(e) => updateField("is_staff", e.target.checked)}
                        color="primary"
                    />
                }
                label="WCA Staff"
            />

            {/* Buttons */}
            <SaveButton
                id="userDetailSaveBtn"
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSave}
            >
                Save
            </SaveButton>
        </Root>
    );
}
